<template>
  <div
    class="container-fluid"
    :class="{ 'detail-page': !useMobileDetail, 'm-detail-page': useMobileDetail }"
  >
      <div class="row" v-if="selectedModel && (item || useAutonomousCustomDetail)">
        <div v-if="selectedModelData && item">
          <!-- ✅ Mensaje informativo para modelos de grupo -->
          <div v-if="isGroupModel()" class="alert bg-primary mt-2 mb-2">
            <i class="fas fa-info-circle me-2"></i>
            <strong>{{ $t('Este contenido es un subitem de otro contenido.') }}</strong>
            {{ $t('Para editar, debes ir al modelo padre y modificar el campo correspondiente.') }}
            <br />
            <button
              class="btn btn-sm btn-secondary mt-2"
              @click="goToParentModel()"
              :title="
                item && item.parent
                  ? $t('Ir a la vista del item padre')
                  : $t('Ir al listado del modelo padre')
              "
            >
              <i class="fas fa-arrow-up me-1"></i>
              {{
                item && item.parent ? $t("Ver Item Padre") : $t("Ir al Modelo Padre")
              }}
            </button>
          </div>
        <!-- Banner de alerta cuando el item está borrado -->
        <div v-if="item.deleted" class="alert alert-danger d-flex align-items-center mb-3 mt-2" role="alert">
          <i class="fas fa-trash-alt fa-2x me-3"></i>
          <div class="flex-grow-1">
            <h5 class="alert-heading mb-1">
              <strong>{{ $t("This item is in the trash") }}</strong>
            </h5>
            <p class="mb-0">{{ $t("This item has been deleted and is in the recycle bin. You can restore it or permanently delete it.") }}</p>
          </div>
          <div v-if=" canEditModel" class="ms-3">
            <button 
              class="btn btn-success btn-sm"
              @click="restoreItem"
            >
              <i class="fas fa-undo me-1"></i>
              {{ $t("Restore from trash") }}
            </button>
          </div>
        </div>
        <ContactOutsideOperationBanner
          v-if="!embedded && item._id"
          :model-name="selectedModel"
          :item-id="String(item._id)"
          :reload-key="item.updatedAt || ''"
        />
        <ShippingIncoherenceBanner
          v-if="!embedded && item._id"
          :model-name="selectedModel"
          :item-id="String(item._id)"
          :reload-key="item.updatedAt || ''"
        />
        <!-- Teléfono: encabezado propio (components/mobile). El de escritorio y el
             formulario no cambian; con useMobileDetail el encabezado de abajo no se renderiza. -->
        <template v-if="useMobileDetail && item">
          <MobileAppBar
            :title="item.name || ''"
            :subtitle="mobileDetailSubtitle"
            :back-to="mobileDetailBackTo"
            :back-label="$t('Back')"
          >
            <template #actions>
              <MissingPrepaymentWarningIcon
                v-if="showMissingPrepaymentTitleWarning"
                :show="true"
                :record="item"
                :tooltip="missingPrepaymentTitleTooltip"
              />
              <MobileIconButton
                icon="sync-alt"
                :label="$t('Refresh content')"
                :spin="isLoading"
                :disabled="isLoading"
                @click="refreshItem"
              />
              <MobileIconButton
                v-if="canEditModel && !useAutonomousCustomDetail"
                icon="edit"
                :label="$t('Edit')"
                @click="editItem()"
              />
              <MobileIconButton
                icon="ellipsis-v"
                :label="$t('mobile.actions')"
                @click="mobileActionsOpen = true"
              />
            </template>
          </MobileAppBar>
          <p class="m-detail-meta">
            {{
              item.updatedAt
                ? $t("updatedby", { name: updatedname, date: formatSubmissionDate(item.updatedAt) })
                : $t("submittedby", { name: authorname, date: formatSubmissionDate(item.createdAt) })
            }}
          </p>
          <MobileBottomSheet
            v-model="mobileActionsOpen"
            :title="item.name || $t('mobile.actions')"
            :close-label="$t('Close')"
          >
            <MobileSheetItem
              v-if="showProformOperacionHeaderAction"
              icon="anchor"
              :label="$t('fobtrading.listOpenOperacionView')"
              @click="runMobileDetailAction(openProformOperacionView)"
            />
            <MobileSheetItem
              v-for="view in publicViews"
              :key="'m-view-' + view._id"
              icon="file-pdf"
              :label="view.name"
              :href="viewHref(view) || ''"
              :new-tab="viewOpensInNewTab(view)"
              :disabled="!viewHref(view)"
              @click="mobileActionsOpen = false"
            />
            <div class="m-sheet-embed" @click="mobileActionsOpen = false">
              <CustomLinks
                :model-name="selectedModel"
                section="view_content"
                :item="item"
              />
              <NotaComercialPdfButton
                v-if="item._id && isNotaComercialModel"
                :nota-id="String(item._id)"
                :nota-comercial-module-active="isNotaComercialModuleActive"
                :flow-direction="String(item.flow_direction || '')"
              />
              <DemandaJurisprudenciaButton
                v-if="!isGroupModel()"
                :selected-model="selectedModel"
                :item="item"
                :can-access-jurisprudencia="canAccessJurisprudenciaModule"
                @referencias-guardadas="onJurisprudenciaReferenciasGuardadas"
              />
            </div>
            <MobileSheetItem
              v-if="!isGroupModel() && canEditModel"
              icon="copy"
              :label="$t('Duplicate')"
              @click="runMobileDetailAction(duplicateItem)"
            />
            <MobileSheetItem
              v-if="!isGroupModel() && canEditModel && item.deleted"
              icon="undo"
              :label="$t('Restore from trash')"
              @click="runMobileDetailAction(restoreItem)"
            />
            <MobileSheetItem
              v-if="canEditModel"
              :icon="item.deleted ? 'trash-alt' : 'trash'"
              :label="item.deleted ? $t('Permanently delete') : $t('Move to trash')"
              danger
              separated
              @click="runMobileDetailAction(item.deleted ? permanentDeleteItem : deleteItem)"
            />
          </MobileBottomSheet>
        </template>
        <div
          v-if="!embedded && item && !useMobileDetail"
          class="w-100 d-flex justify-content-between align-items-center"
        >
           <h4 class="mb-0 mt-3 d-inline-flex align-items-center gap-2 flex-wrap" :class="{ 'text-muted': item.deleted }" :style="item.deleted ? 'text-decoration: line-through;' : ''">
             <span>
               <small>
                 {{
                   selectedModelData.label
                     ? $t(selectedModelData.label)
                     : $t(selectedModelData.modelName)
                 }}</small
               >
               - {{ item.name }}
             </span>
             <MissingPrepaymentWarningIcon
               v-if="showMissingPrepaymentTitleWarning"
               :show="true"
               :record="item"
               :tooltip="missingPrepaymentTitleTooltip"
             />
             <span v-if="item.deleted" class="badge badge-danger text-danger ms-2">
               <i class="fas fa-trash-alt me-1"></i>{{ $t("In trash") }}
             </span>
           </h4> 
           <div class="text-end">
           <small class="d-block text-muted">{{$t('submittedby', { name: authorname, date: formatSubmissionDate(item.createdAt) }) }}</small> 
           <small class="d-block text-muted" v-if="item.updatedAt">{{$t('updatedby', { name: updatedname, date: formatSubmissionDate(item.updatedAt) }) }}</small> 
           </div>
          </div>
          <div
            v-if="!embedded && item && !useMobileDetail"
            class="text-right mb-2 gt-model-actions-toolbar"
          >
            <button
              class="btn btn-sm btn-outline-primary mx-1"
              @click="refreshItem"
              :disabled="isLoading"
              :title="$t('Refresh content')"
            >
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
            </button>
            <span
              v-if="item && !isGroupModel()"
              class="d-inline-block mx-1 align-middle"
            >
              <DemandaJurisprudenciaButton
                :selected-model="selectedModel"
                :item="item"
                :can-access-jurisprudencia="canAccessJurisprudenciaModule"
                @referencias-guardadas="onJurisprudenciaReferenciasGuardadas"
              />
            </span>

            <CustomLinks
              v-if="item"
              data-focus-id="content-pdfs"
              :model-name="selectedModel"
              section="view_content"
              :item="item"
            />
            <NotaComercialPdfButton
              v-if="item && item._id && isNotaComercialModel"
              :nota-id="String(item._id)"
              :nota-comercial-module-active="isNotaComercialModuleActive"
              :flow-direction="String((item && item.flow_direction) || '')"
            />
            <div class="btn-group float-right" v-if="publicViews.length">
              <a
                v-for="view in publicViews"
                :key="view._id"
                class="btn btn-sm btn-outline-primary gt-view-link"
                :class="{ disabled: !viewHref(view) }"
                role="button"
                :href="viewHref(view) || null"
                :target="viewOpensInNewTab(view) ? '_blank' : null"
                :rel="viewOpensInNewTab(view) ? 'noopener' : null"
              >
                {{ view.name }}
              </a>
            </div>
            <div
              v-if="canEditModel && !useAutonomousCustomDetail"
              class="btn-group float-right"
            >
              <button class="btn btn-sm btn-primary" @click="editItem()" data-focus-id="edit-detail">
                <i class="fas fa-edit me-1"></i>
                {{ $t('Edit') }}
              </button>
            </div>
            <button
              v-if="showProformOperacionHeaderAction"
              type="button"
              class="btn btn-sm text-primary mx-1 btn-outline-primary"
              :title="$t('fobtrading.listOpenOperacionView')"
              :aria-label="$t('fobtrading.listOpenOperacionView')"
              @click="openProformOperacionView"
            >
              <i class="fas fa-anchor me-1" aria-hidden="true"></i>
              {{ $t("fobtrading.listOpenOperacionView") }}
            </button>
            <div
              class="btn-group float-right"
              v-if="morebuttons && !isGroupModel() && !embedded && canEditModel"
            >
              <button class="btn btn-sm btn-secondary" @click="duplicateItem">
                <i class="fas fa-copy me-1"></i>
                {{ $t('Duplicate') }}
              </button>
            </div>
            <div
              class="btn-group float-right"
              v-if="morebuttons && !isGroupModel() && !embedded && canEditModel && item?.deleted"
            >
              <button
                class="btn btn-sm btn-success"
                @click="restoreItem"
              >
                <i class="fas fa-undo me-1"></i>
                {{ $t('Restore from trash') }}
              </button>
            </div>
            <div
              class="btn-group float-right"
              v-if="morebuttons && !embedded && canEditModel"
            >
              <button
                class="btn btn-sm"
                :class="item?.deleted ? 'btn-danger' : 'btn-danger'"
                @click="item?.deleted ? permanentDeleteItem() : deleteItem()"
              >
                <i :class="item?.deleted ? 'fas fa-trash-alt' : 'fas fa-trash'" class="me-1"></i>
                {{ item?.deleted ? $t('Permanently delete') : $t('Move to trash') }}
              </button>
            </div>
            <!-- El toggle va siempre último: si queda en el medio, al desplegar se corre y el clic cae en «Move to trash». -->
            <button
              class="btn btn-sm text-primary mx-1 btn-outline-primary"
              @click="morebuttons = true"
              v-if="!embedded && canEditModel && !morebuttons"
            >
              <i class="fas fa-plus"></i> {{ $t("More actions") }}
            </button>
            <button
              class="btn btn-sm bg-white text-primary mx-1"
              @click="morebuttons = false"
              v-if="!embedded && canEditModel && morebuttons"
            >
              <i class="fas fa-minus"></i> {{ $t("Hide actions") }}
            </button>
          </div>
        </div>
        <div
          v-if="useAutonomousCustomDetail"
          style="z-index: 2"
          class="col-12"
          :class="{ 'opacity-75': item && item.deleted }"
          :style="item && item.deleted ? ' padding: 15px; border-radius: 5px; border: 2px dashed #dc3545;' : ''"
        >
          <DynamicModelCustomFormShell
            ref="customFormShell"
            mode="edit"
            suppress-chrome
          />
        </div>
        <div
          v-else-if="selectedModel && item"
          style="z-index: 2"
          class="col-12"
          :class="{ 'opacity-75': item.deleted }"
          :style="item.deleted ? ' padding: 15px; border-radius: 5px; border: 2px dashed #dc3545;' : ''"
        >
            <div v-if="!modelFields.length">{{ $t('No hay campos definidos') }}</div>

            <div class="vertical-tabs-container">
              <!-- Visual Groups: ViewContent solo cuando el modelo tiene visual groups -->
              <div v-if="isVisualGroup()">
                <!-- Campos sin grupo visual: fuera de fieldsets, siempre visibles -->
                <div
                  v-if="modelFields.length && item && !loading && modelFieldsWithoutVisualGroup.length"
                  class="col-md-12 p-0 mb-3"
                >
                  <ViewContent
                    :key="(item && item._id ? `${selectedModel}-${item._id}` : `${selectedModel}-${$route.params.itemId}`) + '-standalone'"
                    :fields="modelFieldsWithoutVisualGroup"
                    :value="item"
                    :visualGroups="[]"
                    :activeVisualGroup="null"
                    :lists="lists"
                    :models="models"
                    :selectedModel="selectedModel"
                    :originalSelectedModel="selectedModel"
                    :isGroupModel="isGroupModel()"
                    :detail-half-width-fields="true"
                    @reference-saved="handleViewContentReferenceSaved"
                  />
                </div>
                <template v-for="visual in sortedVisualGroups">
                  <div
                    v-if="canViewVisualGroup(visual)"
                    :key="visual._id"
                    class="col-md-12 p-0"
                  >
                  <label
                    class="form-label cursor-pointer w-100"
                    :data-focus-id="`visual-group-${String(visual.name || '').toLowerCase()}`"
                    :class="getClassFieldset(visual.name)"
                    @click="setActiveVisualGroup(visual.name)"
                  >
                    <GroupIcon class="me-1" :class="activeVisualGroup === visual.name ? 'text-white' : ''" :icon="visual.icon" />
                    <i v-if="activeVisualGroup === visual.name" class="material-icons text-white me-1">keyboard_arrow_down</i>
                    <i v-else class="material-icons me-1">keyboard_arrow_right</i>
                    {{ $t(visual.label) }}
                  </label>
                  <div v-if="activeVisualGroup === visual.name" class="vertical-tab-content">
                    <ViewContent
                      v-if="modelFields.length && item && !loading && fieldsForVisualGroup(visual.name).length"
                      :key="(item && item._id ? `${selectedModel}-${item._id}` : `${selectedModel}-${$route.params.itemId}`) + '-vg-' + visual.name"
                      :fields="fieldsForVisualGroup(visual.name)"
                      :value="item"
                      :visualGroups="[]"
                      :activeVisualGroup="null"
                      :lists="lists"
                      :models="models"
                      :selectedModel="selectedModel"
                      :originalSelectedModel="selectedModel"
                      :isGroupModel="isGroupModel()"
                      :detail-half-width-fields="true"
                      @reference-saved="handleViewContentReferenceSaved"
                    />
                    <!-- Sin esto el tab se abre vacío mientras carga y parece que no
                         tuviera contenido. Mismo patrón que el tab de contenido relacionado. -->
                    <ContentSkeleton
                      v-else-if="loading || !item || !modelFields.length"
                      variant="detail"
                      :rows="6"
                      padded
                    />
                  </div>
                </div>
                </template>
              </div>
              <!-- Sin visual groups: mostrar ViewContent igualmente para ver el item -->
              <div v-else-if="modelFields.length && item && !loading" class="col-md-12 p-0">
                <ViewContent
                  :key="item && item._id ? `${selectedModel}-${item._id}` : `${selectedModel}-${$route.params.itemId}`"
                  :fields="modelFields"
                  :value="item"
                  :visualGroups="[]"
                  :activeVisualGroup="null"
                  :lists="lists"
                  :models="models"
                  :selectedModel="selectedModel"
                  :originalSelectedModel="selectedModel"
                  :isGroupModel="isGroupModel()"
                  :detail-half-width-fields="true"
                  @reference-saved="handleViewContentReferenceSaved"
                />
              </div>
              
              
              <!-- Tab de Finance / Account Statement -->
              <div
                v-if="isFinancialModel"
                class="col-md-12 p-0 mb-2"
              >
                <label
                  data-focus-id="finance-tab"
                  class="form-label cursor-pointer w-100"
                  :class="getClassFieldset('_finance')"
                  @click="setActiveVisualGroup('_finance')"
                >
                  <i class="material-icons me-1" :class="activeVisualGroup === '_finance' ? 'text-white' : ''">account_balance</i>
                  <i v-if="activeVisualGroup === '_finance'" class="material-icons text-white me-1">keyboard_arrow_down</i>
                  <i v-else class="material-icons me-1">keyboard_arrow_right</i>
                  {{ $t(financeTabLabel) }}
                </label>
                <div v-if="activeVisualGroup === '_finance'" class="vertical-tab-content">
                  <div class="finance-content-section">
                    <!-- ============ Resumen financiero: shipping y Proforma; situación / cuenta corriente: solo Proforma ============ -->
                    <template v-if="isShippingOrProform">
                      <div
                        v-if="showProformOperacionShortcutInFinance"
                        class="d-flex justify-content-end mb-2"
                      >
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-primary"
                          :title="$t('fobtrading.listOpenOperacionView')"
                          :aria-label="$t('fobtrading.listOpenOperacionView')"
                          @click="openProformOperacionView"
                        >
                          <i class="fas fa-external-link-alt me-1" aria-hidden="true"></i>
                          {{ $t("fobtrading.listOpenOperacionView") }}
                        </button>
                      </div>
                      <!-- Financial Summary -->
                      <FinancialSummary
                        v-if="item"
                        ref="financialSummary"
                        :model-name="selectedModel"
                        :item-data="item"
                        :fobtrading-module-active="isFobtradingModuleActive"
                        :nota-comercial-module-active="isNotaComercialModuleActive"
                        :collection-summary-only="isProformModelForFinance"
                        @advance-pct-saved="handleFinancialSummaryAdvancePctSaved"
                        @shipping-saved="handleFinancialSummaryShippingSaved"
                        @financial-data-changed="handleFinancialSummaryDataChanged"
                        @advance-pending-by-shipping="handleAdvancePendingByShipping"
                        @edit-item="handleFinancialSummaryEditItem"
                      />
                    </template>
                    
                    <!-- ============ CONTENIDO PARA CONTACT (sin fieldset, directamente abierto) ============ -->
                    <template v-else-if="selectedModel === 'Contact' && item && item._id">
                      <AccountStatement
                        v-if="item._id && item._id !== 'undefined'"
                        :contactId="item._id"
                      />
                      <div v-else class="alert alert-warning">
                        <i class="fas fa-exclamation-triangle me-2"></i>
                        {{ $t('Contact ID is required to display Account Statement') }}
                      </div>
                    </template>
                    
                    <!-- ============ CONTENIDO PARA BANKACCOUNT (sin fieldset, directamente abierto) ============ -->
                    <template v-else-if="selectedModel === 'BankAccount' && item && item._id && canAccessBankAccountStatement">
                      <BankAccountStatement
                        v-if="item._id && item._id !== 'undefined'"
                        :bankAccountId="item._id"
                      />
                      <div v-else class="alert alert-warning">
                        <i class="fas fa-exclamation-triangle me-2"></i>
                        {{ $t('Bank Account ID is required to display Bank Account Statement') }}
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              
              <!-- Tabs de campos embebidos -->
              <div
                v-for="embedField in embedFields"
                :key="'embed-' + embedField.key"
                class="col-md-12 p-0 mb-2"
              >
                <label
                  class="form-label cursor-pointer w-100"
                  :class="getClassFieldset('_embed_' + embedField.key)"
                  @click="setActiveVisualGroup('_embed_' + embedField.key)"
                >
                  <i v-if="isLoadingEmbed[embedField.key]" class="fas fa-spinner fa-spin me-1" :class="activeVisualGroup === '_embed_' + embedField.key ? 'text-white' : ''"></i>
                  <i v-else class="material-icons me-1" :class="activeVisualGroup === '_embed_' + embedField.key ? 'text-white' : ''">{{ embedField.label2 || 'folder_open' }}</i>
                  <i v-if="activeVisualGroup === '_embed_' + embedField.key" class="material-icons text-white me-1">keyboard_arrow_down</i>
                  <i v-else class="material-icons me-1">keyboard_arrow_right</i>
                  {{ $t(embedField.label) }}
                  <span v-if="embedField.isArray" class="badge ms-1" :class="activeVisualGroup === '_embed_' + embedField.key ? 'bg-white text-primary' : 'bg-primary'">{{ getEmbedCount(embedField) }}</span>
                </label>
                <div v-if="activeVisualGroup === '_embed_' + embedField.key" class="vertical-tab-content">
                  <div class="embed-content-section">
                    <!-- Loading state -->
                    <ContentSkeleton v-if="isLoadingEmbed[embedField.key]" variant="detail" :rows="4" padded />
                    
                    <!-- Embedded items list -->
                    <div v-else-if="embedData[embedField.key] && embedData[embedField.key].length > 0">
                      <div 
                        v-for="(embeddedItem, index) in embedData[embedField.key]" 
                        :key="embeddedItem._id || index"
                        class="mb-3"
                      >
                        <DynamicModelDetail
                          :embedded="true"
                          :prop-model-name="embedField.ref"
                          :prop-item-data="embeddedItem"
                        />
                      </div>
                    </div>
                    
                    <!-- No items -->
                    <div v-else class="text-center py-4">
                      <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
                      <p class="text-muted">{{ $t('No items found.') }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
         
        </div>

        <!-- Timeline: cabecera ligera; el componente (y su API) solo al abrir -->
        <div
          v-if="!embedded && routeItemId && isHistoryModuleActive"
          class="w-100"
        >
          <div
            v-if="!itemHistoryTimelineOpen"
            class="card detail-timeline-card p-0 w-100 mt-3"
          >
            <div
              class="p-2 d-flex justify-content-between align-items-center"
              style="cursor: pointer"
              @click="itemHistoryTimelineOpen = true"
            >
              <span class="mb-0">
                <i class="fas fa-stream text-primary me-1"></i>{{ $t("history.timelineTitle") }}
              </span>
              <i class="fas fa-chevron-down"></i>
            </div>
          </div>
          <HistoryItemTimeline
            v-else
            inline
            start-expanded
            :model-name="selectedModel"
            :item-id="routeItemId"
            :label="item && item.name ? item.name : ''"
            :display-model-label="
              selectedModelData && selectedModelData.label
                ? $t(selectedModelData.label)
                : selectedModel
            "
          />
        </div>
      </div>
      <ContentSkeleton v-else variant="detail" :rows="8" padded />
    

    <ReferenceModal
      v-if="showReferenceModal"
      :is-visible="showReferenceModal"
      :model-name="currentReferenceField?.ref"
      :model-label="getModelLabel(currentReferenceField?.ref)"
      :model-fields="referenceModelFields"
      :lists="lists"
      :models="models"
      :editing-item="currentEditingItem"
      :parentID="routeItemId"
      @saved="handleReferenceSaved"
      @close="closeReferenceModal"
    />

  </div>
</template>

<script>
import GroupIcon from "@/components/gotrading/common/GroupIcon.vue";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import ViewContent from "@/components/gotrading/common/ViewContent.vue";
import Form from "@/components/gotrading/common/Form.vue";
import Breadcrumb from "@/components/gotrading/common/Breadcrumb.vue";
import ReferenceModal from "@/components/gotrading/common/ReferenceModal.vue";
import { HISTORY_MODULE_NAME } from "@/modules/history_module";
import CustomLinks from "@/components/gotrading/common/CustomLinks.vue";
import FinancialSummary from "@/components/gotrading/FinancialSummary.vue";
import PaymentsList from "@/pages/financial/PaymentsList.vue";
import AccountStatement from "@/pages/financial/AccountStatement.vue";
import BankAccountStatement from "@/pages/financial/BankAccountStatement.vue";
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import permissionsMixin from "@/mixins/permissionsMixin";
import DemandaJurisprudenciaButton from "@/modules/jurisprudencia_module/components/DemandaJurisprudenciaButton.vue";
import DynamicModelCustomFormShell from "@/components/gotrading/dynamic/DynamicModelCustomFormShell.vue";
import { isAutonomousDynamicForm } from "@/utils/dynamicFormRegistry";
import {
  resolveDynamicModelRouteParams,
  maybeReplaceWithPrettyItemViewUrl,
} from "@/utils/dynamicModelPaths";
import {
  setCurrentItemLabel,
  clearCurrentItemLabel,
  buildItemDisplayName,
} from "@/utils/currentItemLabel";
import { NOTA_COMERCIAL_MODULE_NAME } from "@/modules/nota_comercial_module";
import NotaComercialPdfButton from "@/modules/nota_comercial_module/components/NotaComercialPdfButton.vue";
import {
  getTrashCommentField,
  deleteCommentFieldHtml,
  buildSoftDeleteRequestConfig,
  bindPermanentDeleteHidesDeleteComment,
  buildTrashDeleteConfirmHtml,
  getTrashDeleteCommentSwalOptions,
  trashDeleteCommentPreConfirm,
  trashDeleteCommentFromSwalResult,
  showTrashDeleteLoadingSwal,
  closeTrashDeleteLoadingSwal,
} from "@/utils/dynamicModelSoftDeleteConfirm";
import { getApiErrorMessage } from "@/utils/dynamicModelValidationSwal";
import {
  PAYMENT_COLLECT_RESPONSABLES_MODULE,
  isPaymentCollectResponsableModel,
  shouldAssignResponsableFromRoute,
  captureAssignResponsableIntent,
  assignResponsableViaApi,
  stripAssignResponsableQuery,
} from "@/modules/payment_collect_responsables_module/utils/assignResponsableFromRoute";
import {
  stripGroupDocumentIds,
  expandGroupIdRefsForDuplicate,
} from "@/components/gotrading/common/referenceModalFormProcessing";
import MissingPrepaymentWarningIcon from "@/components/gotrading/common/MissingPrepaymentWarningIcon.vue";
import ContactOutsideOperationBanner from "@/components/financial/ContactOutsideOperationBanner.vue";
import ShippingIncoherenceBanner from "@/components/gotrading/common/ShippingIncoherenceBanner.vue";
import mobileLayoutMixin from "@/mixins/mobileLayoutMixin";
import MobileAppBar from "@/components/mobile/MobileAppBar.vue";
import MobileIconButton from "@/components/mobile/MobileIconButton.vue";
import MobileBottomSheet from "@/components/mobile/MobileBottomSheet.vue";
import MobileSheetItem from "@/components/mobile/MobileSheetItem.vue";
import {
  buildMissingPrepaymentTooltip,
  hasMissingAssociatedPrepayment,
  isProformOrShippingModelName,
} from "@/utils/missingPrepaymentWarning";

export default {
  mixins: [permissionsMixin, mobileLayoutMixin],
  name: "DynamicModelDetail",
  inject: {
    dashboardLayout: { default: null },
  },
  components: {
    ContentSkeleton,
    GroupIcon,
    ViewContent,
    Breadcrumb,
    Form,
    ReferenceModal,
    HistoryItemTimeline: () =>
      import("@/modules/history_module/components/HistoryItemTimeline.vue"),
    CustomLinks,
    FinancialSummary,
    PaymentsList,
    AccountStatement,
    BankAccountStatement,
    DemandaJurisprudenciaButton,
    DynamicModelCustomFormShell,
    NotaComercialPdfButton,
    MissingPrepaymentWarningIcon,
    ContactOutsideOperationBanner,
    ShippingIncoherenceBanner,
    MobileAppBar,
    MobileIconButton,
    MobileBottomSheet,
    MobileSheetItem,
    // Referencia a sí mismo para uso recursivo (shippings embebidos)
    DynamicModelDetail: () => import('./dynamic-model-detail.vue'),
  },
  props: {
    // Props para usar el componente embebido (sin cargar desde URL)
    propModelName: {
      type: String,
      default: null,
    },
    propItemData: {
      type: Object,
      default: null,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mobileActionsOpen: false,
      isExpanded: false,
      morebuttons: false,
      models: [],
      selectedModel: "",
      selectedModelData: null,
      authorname: 'anonymous',
      updatedname: 'anonymous',
      modelFields: [],
      item: null,
      lists: [],
      loading: true,
      useAutonomousCustomDetail: false,
      referenceItems: {},
      modelViews: [],
      showReferenceModal: false,
      currentReferenceField: null,
      referenceModelFields: [],
      isLoading: false,
      // { key, promise } de la petición del item en curso (ver fetchItem).
      itemFetchInFlight: null,
      /** Evita recargas superpuestas del documento tras cambios del resumen financiero */
      isReloadingAfterFinancialChange: false,
      /** Anticipo pendiente por embarque (id → monto) que informa el resumen financiero */
      advancePendingByShipping: null,
      currentEditingItem: null,
      /** true mientras el ReferenceModal está abierto por "Duplicar" (tras guardar, navegar al registro nuevo) */
      duplicateModalActive: false,
      isDuplicating: false,
      activeVisualGroup: null, // Estado para controlar qué grupo visual está activo
      settings: null,
      // Datos para los tabs embebidos
      embedData: {}, // { fieldKey: [items] }
      isLoadingEmbed: {}, // { fieldKey: boolean }
      // Datos para verificación de permisos de módulos
      activeModules: [],
      /** Timeline de historial: no montar ni fetch hasta que el usuario abra la card */
      itemHistoryTimelineOpen: false,
      userRoles: [],
    };
  },
  watch: {
    "$route.query": {
      deep: true,
      handler() {
        this.$nextTick(() => this.applyRouteVisualGroupHint());
      },
    },
    routeModelName: {
      immediate: true,
      handler() {
        if (this.embedded) return;
        this.syncSelectedModelFromRoute();
      },
    },
    routeItemId: {
      immediate: true,
      handler(newVal, oldVal) {
        // No usar route params si estamos en modo embebido
        if (this.embedded) return;

        // Timeline lazy: al cambiar de ítem, volver a cabecera cerrada (sin fetch).
        if (newVal !== oldVal) {
          this.itemHistoryTimelineOpen = false;
        }

        const model = this.syncSelectedModelFromRoute();
        if (newVal && model) {
          this.fetchItem(newVal);
        }
      },
    },
    // Watch para props embebidas
    propItemData: {
      immediate: true,
      handler(newVal) {
        if (this.embedded && newVal) {
          this.item = newVal;
          this.loading = false;
        }
      },
    },
    models: {
      handler(newVal) {
        const { modelName } = resolveDynamicModelRouteParams(
          this.$route,
          (newVal || []).map((m) => m.modelName)
        );
        if (modelName && newVal.length && !this.selectedModel) {
          const found = newVal.find(
            (m) => m.modelName.toLowerCase() === modelName.toLowerCase()
          );
          if (found) {
            this.selectedModel = found.modelName;
            this.fetchFields();
          }
        }
      },
    },
    selectedModel: {
      handler(newVal, oldVal) {
        if (!newVal) return;
        if (newVal !== oldVal || !this.modelViews.length) {
          this.fetchModelViews();
        }
      },
    },
    item: {
      handler(newVal, oldVal) {
        this.updateTitle();
        this.$nextTick(() => this.applyRouteVisualGroupHint());
      },
    },
  },
  computed: {
    /** Encabezado de teléfono (components/mobile): solo en la página, no embebido. */
    useMobileDetail() {
      return this.isMobileLayout && !this.embedded;
    },
    /** Volver al listado del modelo (en el teléfono no hay miga de pan). */
    mobileDetailBackTo() {
      return this.selectedModel ? `/dynamic-model/${this.selectedModel}` : null;
    },
    mobileDetailSubtitle() {
      if (!this.selectedModelData) return "";
      const label = this.selectedModelData.label
        ? this.$t(this.selectedModelData.label)
        : this.$t(this.selectedModelData.modelName);
      return this.item && this.item.deleted ? `${label} · ${this.$t("In trash")}` : label;
    },
    routeModelName() {
      if (this.embedded) {
        return this.selectedModel || "";
      }
      const { modelName } = resolveDynamicModelRouteParams(
        this.$route,
        (this.models || []).map((m) => m.modelName)
      );
      return modelName || this.selectedModel || "";
    },
    routeItemId() {
      if (this.embedded) {
        return this.item && this.item._id ? String(this.item._id) : "";
      }
      const { itemId } = resolveDynamicModelRouteParams(
        this.$route,
        (this.models || []).map((m) => m.modelName)
      );
      if (itemId) return itemId;
      return this.item && this.item._id ? String(this.item._id) : "";
    },
    showMissingPrepaymentTitleWarning() {
      if (!this.item) return false;
      const model =
        this.selectedModel ||
        (this.selectedModelData && this.selectedModelData.modelName) ||
        this.routeModelName;
      if (!isProformOrShippingModelName(model)) return false;
      if (hasMissingAssociatedPrepayment(this.item)) return true;
      const pendingByShip = this.advancePendingByShipping;
      if (pendingByShip && typeof pendingByShip === "object") {
        return Object.keys(pendingByShip).some(
          (id) => Number(pendingByShip[id]) > 0
        );
      }
      return false;
    },
    missingPrepaymentTitleTooltip() {
      if (!this.showMissingPrepaymentTitleWarning) return "";
      return buildMissingPrepaymentTooltip(
        this.item,
        this.$t("Sin prepayment asociado"),
        this.advancePendingByShipping
      );
    },
    // Verificar si el usuario puede editar el modelo actual
    canEditModel() {
      if (!this.selectedModel) return false;
      const modelNameLower = this.selectedModel.toLowerCase();
      return this.hasPermission(`${modelNameLower}.manage`);
    },
    canAccessJurisprudenciaModule() {
      const d = this.dashboardLayout;
      return d && typeof d.canAccessJurisprudenciaModule === "boolean"
        ? d.canAccessJurisprudenciaModule
        : false;
    },
    // Verificar si el usuario puede acceder al Bank Account Statement
    canAccessBankAccountStatement() {
      // Si tiene finance.manage, puede hacer todo
      if (this.hasPermission('finance.manage')) {
        return true;
      }
      // Si tiene finance.bankAccountStatement, puede ver el statement
      return this.hasPermission('finance.bankAccountStatement');
    },
    // Check if financial module is active
    isHistoryModuleActive() {
      return this.activeModules.some(
        (module) => module.name === HISTORY_MODULE_NAME
      );
    },
    isFinancialModuleActive() {
      return this.activeModules.some(
        (module) => module.name === "financial_module"
      );
    },
    isFobtradingModuleActive() {
      return this.activeModules.some(
        (module) => module.name === "fobtrading_module"
      );
    },
    isNotaComercialModuleActive() {
      return this.activeModules.some(
        (module) => module.name === NOTA_COMERCIAL_MODULE_NAME
      );
    },
    isNotaComercialModel() {
      return String(this.selectedModel || "").toLowerCase() === "notacomercial";
    },
    isPaymentCollectResponsablesModuleActive() {
      return this.activeModules.some(
        (module) => module.name === PAYMENT_COLLECT_RESPONSABLES_MODULE
      );
    },
    // Check if user can access financial module
    canAccessFinancialModule() {
      // Primero verificar si el módulo está activo - si no está activo, nadie puede acceder
      if (!this.isFinancialModuleActive) {
        return false;
      }

      // Si el usuario es admin, tiene acceso
      if (localStorage.getItem("role") === "admin") {
        return true;
      }

      // Verificar si el rol del usuario tiene acceso al módulo
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }

      // Buscar en los roles cargados si están disponibles
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes("financial_module");
        }
      }

      // Lógica de respaldo para roles específicos
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    // Check if current model should show financial tab (Proform, shipping, Contact or BankAccount)
    // AND user has access to financial module AND module is active
    isFinancialModel() {
      if (!this.selectedModel) return false;
      const modelLower = this.selectedModel.toLowerCase();
      const isFinancialModelType = modelLower === 'proform' || modelLower === 'shipping' || modelLower === 'contact' || modelLower === 'bankaccount';
      
      // Si no es un modelo financiero, no mostrar el tab
      if (!isFinancialModelType) return false;
      
      // Si el módulo no está activo, no mostrar el tab
      if (!this.isFinancialModuleActive) return false;
      
      // Si el usuario no tiene acceso al módulo financiero, no mostrar el tab
      if (!this.canAccessFinancialModule) return false;
      
      // Para BankAccount, verificar además el permiso específico de bankAccountStatement
      if (modelLower === 'bankaccount') {
        return this.canAccessBankAccountStatement;
      }
      
      // Para otros modelos financieros (proform, shipping, contact), mostrar el tab
      return true;
    },
    // Get the label for the finance tab based on model type
    financeTabLabel() {
      if (!this.selectedModel) return 'Finance';
      const modelLower = this.selectedModel.toLowerCase();
      if (modelLower === 'contact' || modelLower === 'bankaccount') {
        return 'Account Statement';
      }
      return 'Finance';
    },
    // Check if model is shipping or Proform (for showing collapsible financial situation)
    isShippingOrProform() {
      if (!this.selectedModel) return false;
      const modelLower = this.selectedModel.toLowerCase();
      return modelLower === 'proform' || modelLower === 'shipping';
    },
    /** Muestra el acordeón «Financial situation» (estado de cuenta) solo en vista Proforma, no en shipping. */
    isProformModelForFinance() {
      if (!this.selectedModel) return false;
      return this.selectedModel.toLowerCase() === 'proform';
    },
    showProformOperacionHeaderAction() {
      return !this.embedded && this.showProformOperacionShortcutInFinance;
    },
    showProformOperacionShortcutInFinance() {
      return (
        this.isProformModelForFinance &&
        this.isFobtradingModuleActive &&
        !!(this.item && this.item._id)
      );
    },
    // Proforma id for embedded general account statement (Proform = self; shipping = custom_proform)
    proformIdForFilter() {
      if (!this.item) return null;
      
      if (this.selectedModel === 'Proform') {
        return this.item._id;
      }
      
      if (this.selectedModel === 'shipping' && this.item.custom_proform) {
        // Handle both cases: object with _id or direct ID
        if (typeof this.item.custom_proform === 'object' && this.item.custom_proform._id) {
          return this.item.custom_proform._id;
        }
        return this.item.custom_proform;
      }
      
      return null;
    },
    /** Label for embedded general account statement (proforma name when available). */
    lockedProformNameForStatement() {
      if (!this.item) return "";
      if (this.selectedModel === "Proform") {
        return this.item.name || "";
      }
      if (
        this.selectedModel === "shipping" &&
        this.item.custom_proform &&
        typeof this.item.custom_proform === "object"
      ) {
        return this.item.custom_proform.name || "";
      }
      return "";
    },
    // contactId (legacy / other tabs)
    contactIdForFilter() {
      if (!this.item) return null;
      
      if (this.selectedModel === 'Contact') {
        return this.item._id;
      }
      
      return null;
    },
    // shippingId (legacy / other uses)
    shippingIdForFilter() {
      if (!this.item) return null;
      
      if (this.selectedModel === 'shipping') {
        return this.item._id;
      }
      
      return null;
    },
    // Campos con embed: true que tienen datos
    embedFields() {
      if (!this.modelFields || !this.item) return [];
      return this.modelFields.filter(field => {
        if (field.type !== 'reference' || !field.embed) return false;
        const data = this.item[field.key];
        // Soporta tanto arrays como valores únicos
        if (field.isArray) {
          return data && Array.isArray(data) && data.length > 0;
        } else {
          return data && data !== null && data !== '';
        }
      });
    },
    // Obtener cantidad de items para un campo embed
    getEmbedCount() {
      return (field) => {
        const data = this.item[field.key];
        if (field.isArray) {
          return Array.isArray(data) ? data.length : 0;
        }
        return data ? 1 : 0;
      };
    },
    hiddenReferenceFields() {
      if (!this.modelFields) return [];
      return this.modelFields.filter(
        (field) =>
          field.type === "reference" &&
          field.hide === true &&
          !field.addbutton &&
          field.ref
      );
    },
    publicViews() {
      return this.modelViews.filter(
        (view) => view.type !== "report" && view.isPublic
      );
    },
    sortedVisualGroups() {
      if (!this.selectedModelData || !this.selectedModelData.visualGroups)
        return [];
      return [...this.selectedModelData.visualGroups].sort((a, b) => {
        const orderA = a.order || 1;
        const orderB = b.order || 1;
        return orderA - orderB;
      });
    },
    /** Top-level fields sin visualGroup o con grupo no definido en el modelo (siempre visibles fuera de fieldsets) */
    modelFieldsWithoutVisualGroup() {
      if (!this.modelFields?.length) return [];
      const defined = new Set(
        (this.sortedVisualGroups || [])
          .map((g) => g.name)
          .filter(Boolean)
      );
      return this.modelFields.filter(
        (f) => !f.visualGroup || !defined.has(f.visualGroup)
      );
    },
  },
  methods: {
    /** Cierra la hoja de acciones y después corre la acción (algunas abren confirmaciones). */
    runMobileDetailAction(fn) {
      this.mobileActionsOpen = false;
      this.$nextTick(() => fn.call(this));
    },

    openProformOperacionView() {
      if (!this.item || !this.item._id) return;
      this.$router.push(`/operacion/${this.item._id}`);
    },

    toggleHistory() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded) {
        this.fetchHistory();
      }
    },
    getClassButton() {
      if (this.selectedModel === "Proform") {
        return "btn-danger";
      }
      if (this.selectedModel === "shipping") {
        return "btn-success";
      }
      return "";
    },
    getClassFieldset(groupName) {
      if (this.activeVisualGroup === groupName) {
        return (
          "detail-section-head is-open bg-primary text-white border p-2 mb-0 border-primary " +
          this.selectedModel.toLowerCase()
        );
      } else {
        return "detail-section-head border p-2";
      }
    },
    async fetchModels() {
      const { data } = await wencesApi.get("/api/gotrading/model-definitions");
      this.models = data;
    },
    async fetchFields() {
      this.modelFields = [];
      if (!this.selectedModel) return;

      // ✅ Detectar si es un modelo de grupo
      if (this.selectedModel.includes("_")) {
        try {
          console.log(
            "🔍 [FRONTEND] Detectado modelo de grupo en detail:",
            this.selectedModel
          );
          const [parentModel, fieldName] = this.selectedModel.split("_");

          this.selectedModelData = {
            modelName: this.selectedModel,
            label: this.selectedModel.replace("_", " - "),
            fields: [], // Fields will be loaded when accessing the item
            isGroupModel: true,
            parentModel: parentModel,
            parentField: fieldName,
          };

          console.log(
            "✅ [FRONTEND] Modelo de grupo configurado en detail:",
            this.selectedModelData
          );
          this.modelFields = [];

          // Only fetch item if we have itemId and haven't loaded it yet
          if (this.$route.params.itemId && !this.item) {
            this.fetchItem(this.$route.params.itemId);
          }
          return;
        } catch (error) {
          console.error("Error configurando modelo de grupo:", error);
        }
      }

      // Caso normal (no es modelo de grupo)
      const model = this.models.find(
        (m) => m.modelName.toLowerCase() === this.selectedModel.toLowerCase()
      );

      if (model) {
        this.selectedModelData = model;
        this.modelFields = model.fields;

        // Only fetch item if we have itemId and haven't loaded it yet
        if (this.$route.params.itemId && !this.item) {
          this.fetchItem(this.$route.params.itemId);
        }
      }
    },
    async refreshItem() {
      if (this.item && this.item._id) {
        await this.fetchItem(this.item._id);
      } else if (this.$route.params.itemId) {
        await this.fetchItem(this.$route.params.itemId);
      }
      const shell = this.$refs.customFormShell;
      if (shell && typeof shell.reloadItem === "function") {
        await shell.reloadItem();
      }
    },
    async onJurisprudenciaReferenciasGuardadas() {
      if (this.item && this.item._id) {
        await this.fetchItem(this.item._id);
      } else if (this.$route.params.itemId) {
        await this.fetchItem(this.$route.params.itemId);
      }
    },
    syncSelectedModelFromRoute() {
      if (this.embedded) return this.selectedModel;
      const { modelName } = resolveDynamicModelRouteParams(
        this.$route,
        (this.models || []).map((m) => m.modelName)
      );
      if (!modelName || !this.models.length) return this.selectedModel;
      const found = this.models.find(
        (m) => m.modelName.toLowerCase() === modelName.toLowerCase()
      );
      if (!found) return this.selectedModel;
      if (this.selectedModel !== found.modelName) {
        this.selectedModel = found.modelName;
        this.selectedModelData = found;
        this.modelFields = found.fields || [];
        this.modelViews = [];
        this.fetchFields();
      }
      return found.modelName;
    },
    /**
     * El watcher `routeItemId` (immediate) y `mounted()` piden el mismo item en el mismo
     * instante, así que salían dos GET idénticos. No es solo una llamada de más: el
     * backend repara grupos embebidos dentro de ese GET, y dos a la vez son dos
     * reparaciones compitiendo sobre el mismo documento.
     */
    async fetchItem(itemId) {
      const targetModel = this.syncSelectedModelFromRoute() || this.selectedModel;
      if (!targetModel || !itemId) return;

      const key = `${targetModel}/${itemId}`;
      if (this.itemFetchInFlight && this.itemFetchInFlight.key === key) {
        return this.itemFetchInFlight.promise;
      }

      const promise = this.loadItemFromApi(targetModel, itemId);
      this.itemFetchInFlight = { key, promise };
      try {
        return await promise;
      } finally {
        if (this.itemFetchInFlight && this.itemFetchInFlight.key === key) {
          this.itemFetchInFlight = null;
        }
      }
    },
    async loadItemFromApi(targetModel, itemId) {
      try {
        this.isLoading = true;
        if (this.item && String(this.item._id) !== String(itemId)) {
          this.advancePendingByShipping = null;
        }
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/${targetModel}/${itemId}`
        );
        this.item = data;
        // El Breadcrumb lee de acá en vez de pedir el item por su cuenta.
        setCurrentItemLabel(this.item && this.item._id, buildItemDisplayName(this.item));
        maybeReplaceWithPrettyItemViewUrl(
          this.$router,
          this.$route,
          targetModel,
          this.item
        );
        if (this.item.createdBy) {
          this.authorname = await this.loadAuthorName(this.item.createdBy);
        }
        if (this.item.updatedBy) {
          this.updatedname = await this.loadAuthorName(this.item.updatedBy);
        }
        if (this.item.editHistory) {
          delete this.item.editHistory;
        }
      } catch (error) {
        console.error("Error loading item:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchLists() {
      const { data } = await wencesApi.get("/api/gotrading/lists");
      this.lists = data;
    },
    editItem() {
      const itemId = this.item?._id || this.$route.params.itemId;
      this.$router.push({
        name: "DynamicModelEdit",
        params: {
          modelName: this.selectedModel,
          itemId: itemId,
        },
      });
    },
    async loadAuthorName(userId) {
      try {
        const { data } = await wencesApi.get(`/api/gotrading/dynamic/users/${userId}`);
        return data?.name || 'anonymous';
      } catch (error) {
        console.error("Error loading author:", error);
        return 'anonymous';
      }
    },
    getModelLabel(modelName) {
      const model = this.models.find((m) => m.modelName === modelName);
      return model ? model.label || model.modelName : modelName;
    },
    async fetchModelViews() {
      try {
        const { data } = await wencesApi.get("/api/gotrading/views");
        this.modelViews = data.filter(
          (view) => view.modelName === this.selectedModel
        );
      } catch (error) {
        console.error("Error loading model views:", error);
      }
    },
    /** Las vistas de contenido (viewhtml, query_report) abren en pestaña nueva; el resto, en la misma. */
    viewOpensInNewTab(view) {
      return !!view && view.type !== "report" && view.type !== "combined_view";
    },
    /**
     * URL de la vista pública, para pintar los botones como <a href> (link real:
     * se puede abrir en pestaña nueva, copiar y recargar).
     *
     * El id sale de `routeItemId`, no de `$route.params.itemId`: al navegar entre dos
     * URLs legibles (/i/:itemSlug) vue-router reutiliza el mismo record y no vuelve a
     * correr el guard que rellena `params.itemId`, así que quedaba vacío. Con el param
     * vacío `$router.resolve` devuelve "/" y el botón terminaba en el dashboard.
     */
    viewHref(view) {
      if (!view || !view._id || !view.modelName) return "";
      const modelName = encodeURIComponent(view.modelName);
      if (view.type === "report") {
        return `/dynamic-model/${modelName}/report/${view._id}`;
      }
      if (view.type === "combined_view") {
        return `/dynamic-model/${modelName}/combined/${view._id}`;
      }
      const itemId = this.routeItemId;
      if (!itemId) return "";
      return `/dynamic-model/${modelName}/display/${encodeURIComponent(
        itemId
      )}/${view._id}`;
    },
    async openReferenceModal(field) {
      this.duplicateModalActive = false;
      this.currentReferenceField = field;
      try {
        // Use existing model definition if available, otherwise fetch it
        let modelDefinition;
        const existingModel = this.models.find(
          (m) => m.modelName === field.ref
        );
        if (existingModel) {
          modelDefinition = existingModel;
        } else {
          const { data } = await wencesApi.get(
            `/api/gotrading/model-definitions/${field.ref}?parentID=${this.$route.params.itemId}&parentModel=${this.selectedModel}`
          );
          modelDefinition = data;
        }
        this.referenceModelFields = modelDefinition.fields;

        // Cargar el mapeo de referencias
        const { data: referenceMapping } = await wencesApi.get(
          `/api/gotrading/model-reference-mapping`,
          {
            params: {
              sourceModel: this.selectedModel,
              targetModel: field.ref,
            },
          }
        );

        // Crear el objeto base con custom_proform
        this.currentEditingItem = {
          custom_proform: this.$route.params.itemId,
        };

        // Aplicar el mapeo si existe
        if (referenceMapping.data && referenceMapping.data.length > 0) {
          referenceMapping.data[0].mappings.forEach((mapping) => {
            if (this.item && this.item[mapping.sourceField]) {
              let value = this.item[mapping.sourceField];

              // Si es un array, limpiar los campos del sistema de cada item
              if (Array.isArray(value)) {
                value = value.map((item) => {
                  const cleanItem = { ...item };
                  // Si tiene un array de products, limpiar cada item
                  if (cleanItem.products && Array.isArray(cleanItem.products)) {
                    cleanItem.products = cleanItem.products.map((product) => {
                      const cleanProduct = { ...product };
                      delete cleanProduct._id;
                      delete cleanProduct.editHistory;
                      delete cleanProduct.createdAt;
                      delete cleanProduct.updatedAt;
                      delete cleanProduct.__v;
                      return cleanProduct;
                    });
                  }
                  return cleanItem;
                });
              }
              this.currentEditingItem[mapping.targetField] = value;
            }
          });
        }

        this.showReferenceModal = true;
      } catch (error) {
        console.error("Error loading reference model or mapping:", error);
      }
    },
    closeReferenceModal() {
      this.showReferenceModal = false;
      this.duplicateModalActive = false;
      this.currentReferenceField = null;
      this.referenceModelFields = [];
    },
    async handleReferenceSaved(savedItem) {
      if (this.duplicateModalActive && savedItem && savedItem._id) {
        this.duplicateModalActive = false;
        const newId = String(savedItem._id);
        await this.$router.push({
          name: "DynamicModelDetail",
          params: {
            modelName: this.selectedModel,
            itemId: newId,
          },
        });
        return;
      }

      // Actualizar el item actual con la nueva referencia (campo reference con key)
      if (
        this.currentReferenceField &&
        this.currentReferenceField.key &&
        this.item
      ) {
        if (!this.item[this.currentReferenceField.key]) {
          this.item[this.currentReferenceField.key] = [];
        }
        this.item[this.currentReferenceField.key].push(savedItem._id);
        try {
          await wencesApi.put(
            `/api/gotrading/dynamic/${this.selectedModel}/${this.item._id}`,
            this.item
          );
          this.closeReferenceModal();
          // No need to reload the item since we already have the updated data
        } catch (error) {
          console.error("Error updating item with new reference:", error);
        }
      }
    },
    handleViewContentReferenceSaved(savedItem) {
      // When a reference is saved from ViewContent, refresh FinancialSummary
      // if we're viewing a shipping or proform (since payments/collections affect financial data)
      if (savedItem && this.isShippingOrProform) {
        // Refresh FinancialSummary to update all financial numbers
        if (this.$refs.financialSummary && typeof this.$refs.financialSummary.refreshData === 'function') {
          this.$refs.financialSummary.refreshData();
        }
        
        // Also refresh the item data to ensure consistency
        if (this.item && this.item._id) {
          this.fetchItem(this.item._id);
        }
      }
    },
    /** Resumen financiero guardó el % de adelanto en la proforma (campo advance): sincronizar vista */
    handleFinancialSummaryAdvancePctSaved() {
      if (this.item && this.item._id) {
        this.fetchItem(this.item._id);
      }
    },
    handleFinancialSummaryEditItem(payload) {
      if (!payload || !payload.modelName || !payload.itemId) return;
      this.$router.push({
        name: "DynamicModelEdit",
        params: {
          modelName: payload.modelName,
          itemId: String(payload.itemId),
        },
      });
    },

    handleFinancialSummaryShippingSaved() {
      if (this.item && this.item._id) {
        this.fetchItem(this.item._id);
      }
    },

    /**
     * Cobro creado/editado/eliminado en el resumen financiero: recargar el documento
     * para que los campos que calcula el backend (p. ej. remaining_advance del aviso
     * de anticipo) queden al día sin recargar la página.
     */
    handleAdvancePendingByShipping(pendingByShippingId) {
      this.advancePendingByShipping =
        pendingByShippingId && typeof pendingByShippingId === "object"
          ? pendingByShippingId
          : null;
    },

    handleFinancialSummaryDataChanged() {
      if (!this.item || !this.item._id) return;
      if (this.isReloadingAfterFinancialChange) return;
      this.isReloadingAfterFinancialChange = true;
      this.fetchItem(this.item._id).finally(() => {
        this.isReloadingAfterFinancialChange = false;
      });
    },
    async duplicateItem() {
      if (!this.item || !this.selectedModel) return;

      this.isDuplicating = true;
      try {
        // Create a deep copy of the item
        const itemCopy = JSON.parse(JSON.stringify(this.item));

        // Remove system fields
        delete itemCopy._id;
        delete itemCopy.editHistory;
        delete itemCopy.createdAt;
        delete itemCopy.updatedAt;
        delete itemCopy.__v;
        delete itemCopy.createdBy;
        delete itemCopy.updatedBy;
        delete itemCopy.idModel;

        if(this.selectedModel === "Proform") {
          delete itemCopy.shippings;
          delete itemCopy.proform_number;
          delete itemCopy.proform_number_date;
          // No reutilizar vínculos/totales financieros de la proforma origen
          // (si se copian, el borrado/UI ven cobros que en realidad son de otra).
          delete itemCopy.collect;
          delete itemCopy.payment;
          delete itemCopy.total_collected;
          delete itemCopy.total_collected_advance;
          delete itemCopy.total_paid;
          delete itemCopy.total_to_collect;
          delete itemCopy.expected_advance;
          delete itemCopy.remaining_advance;
          delete itemCopy.total_remaining_advance;
          delete itemCopy.collection_advanced_date;
        }

        if (this.selectedModel === "Shipping" || this.selectedModel === "shipping") {
          delete itemCopy.collect;
          delete itemCopy.payment;
        }

        await expandGroupIdRefsForDuplicate(
          itemCopy,
          this.modelFields,
          this.selectedModel,
          (groupModelName, id) =>
            wencesApi
              .get(`/api/gotrading/dynamic/${groupModelName}/${id}`)
              .then((res) => res.data)
        );

        // Force new group rows (e.g. productandprice); never reuse source _ids/parent
        stripGroupDocumentIds(itemCopy, this.modelFields, { force: true });

        // Set up the reference modal with current model information
        this.duplicateModalActive = true;
        this.currentReferenceField = {
          ref: this.selectedModel,
        };
        this.referenceModelFields = this.modelFields;
        this.currentEditingItem = itemCopy;
        this.showReferenceModal = true;
      } catch (error) {
        console.error("Error duplicating item:", error);
      } finally {
        this.isDuplicating = false;
      }
    },
    async deleteItem() {
      if (!this.item || !this.selectedModel) return;

      const trashCommentField = getTrashCommentField(this.modelFields);
      const commentHtml = trashCommentField
        ? deleteCommentFieldHtml(this.$t.bind(this), trashCommentField)
        : "";

      // Show confirmation dialog using SweetAlert
      const result = await Swal.fire({
        title: this.$tn("Are you sure?"),
        html: trashCommentField
          ? buildTrashDeleteConfirmHtml({
              messageHtml: `<p>${this.$tn("Do you want to move this")} ${this.selectedModelData?.label || this.$tn("record")} ${this.$tn("to the trash? You can recover it later.")}</p>${commentHtml}<div style="margin-top: 15px;"><label style="display: flex; align-items: center; cursor: pointer;"><input type="checkbox" id="swal-delete-permanently-detail" style="margin-right: 8px; cursor: pointer;"><span>${this.$tn("Delete permanently")}</span></label></div>`,
            })
          : `
          <div style="text-align: left;">
            <p>${this.$tn("Do you want to move this")} ${this.selectedModelData?.label || this.$tn("record")} ${this.$tn("to the trash? You can recover it later.")}</p>
            <div style="margin-top: 15px;">
              <label style="display: flex; align-items: center; cursor: pointer;">
                <input type="checkbox" id="swal-delete-permanently-detail" style="margin-right: 8px; cursor: pointer;">
                <span>${this.$tn("Delete permanently")}</span>
              </label>
            </div>
          </div>
        `,
        ...(trashCommentField ? getTrashDeleteCommentSwalOptions() : {}),
        ...(trashCommentField ? { preConfirm: trashDeleteCommentPreConfirm } : {}),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$tn("Yes, move to trash"),
        cancelButtonText: this.$tn("Cancel"),
        didOpen: () => {
          const checkbox = document.getElementById('swal-delete-permanently-detail');
          if (checkbox) {
            checkbox.addEventListener('change', (e) => {
              const confirmButton = Swal.getConfirmButton();
              if (e.target.checked) {
                confirmButton.textContent = this.$tn("Yes, permanently delete");
                confirmButton.style.backgroundColor = "#8b0000";
              } else {
                confirmButton.textContent = this.$tn("Yes, move to trash");
                confirmButton.style.backgroundColor = "#d33";
              }
            });
          }
          if (trashCommentField) {
            bindPermanentDeleteHidesDeleteComment();
          }
        },
      });

      if (result.isConfirmed) {
        const deletePermanently = document.getElementById('swal-delete-permanently-detail')?.checked || false;
        const trashDeleteComment = trashCommentField
          ? trashDeleteCommentFromSwalResult(result)
          : "";
        try {
          const endpoint = deletePermanently
            ? `/api/gotrading/dynamic/${this.selectedModel}/${this.item._id}/permanent`
            : `/api/gotrading/dynamic/${this.selectedModel}/${this.item._id}`;

          const deleteConfig = trashDeleteComment
              ? buildSoftDeleteRequestConfig(trashDeleteComment)
              : {};

          showTrashDeleteLoadingSwal();
          try {
            await wencesApi.delete(endpoint, deleteConfig);
          } finally {
            closeTrashDeleteLoadingSwal();
          }
          
          Swal.fire({
            title: deletePermanently ? this.$tn("Deleted") : this.$tn("Moved to trash"),
            text: deletePermanently 
              ? this.$tn("The item has been permanently deleted.")
              : this.$tn("The item has been moved to trash successfully."),
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
          // Redirect to the list view after a short delay
          setTimeout(() => {
            this.$router.push({
              name: "DynamicModel",
              params: { modelName: this.selectedModel },
            });
          }, 1500);
        } catch (error) {
          const fallback = deletePermanently
            ? this.$t("An error occurred while deleting the item.")
            : this.$t("An error occurred while moving the item to trash.");
          Swal.fire(
            this.$tn("Error"),
            getApiErrorMessage(error, fallback),
            "error"
          );
        }
      }
    },
    async restoreItem() {
      if (!this.item || !this.selectedModel) return;

      // Verificar que el item esté en la papelera
      if (!this.item.deleted) {
        await Swal.fire({
          title: this.$tn("Error"),
          text: this.$tn("This item is not in the trash."),
          icon: "error",
        });
        return;
      }

      const result = await Swal.fire({
        title: this.$tn("Restore?"),
        text: `${this.$tn("Do you want to restore this")} ${this.selectedModelData?.label || this.$tn("record")}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#6c757d",
        confirmButtonText: this.$tn("Yes, restore"),
        cancelButtonText: this.$tn("Cancel"),
      });

      if (result.isConfirmed) {
        try {
          await wencesApi.post(
            `/api/gotrading/dynamic/${this.selectedModel}/${this.item._id}/restore`
          );
          await Swal.fire({
            title: this.$tn("Restored"),
            text: this.$tn("The item has been restored successfully."),
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
          // Recargar el item para actualizar el estado
          await this.fetchItem(this.item._id);
        } catch (error) {
          console.error("Error restoring:", error);
          await Swal.fire({
            title: this.$tn("Error"),
            text: this.$tn("An error occurred while restoring the item."),
            icon: "error",
          });
        }
      }
    },
    async permanentDeleteItem() {
      if (!this.item || !this.selectedModel) return;

      // Verificar que el item esté en la papelera
      if (!this.item.deleted) {
        await Swal.fire({
          title: this.$tn("Error"),
          text: this.$tn("This item is not in the trash. Use the normal delete to move it to trash first."),
          icon: "error",
        });
        return;
      }

      const trashCommentField = getTrashCommentField(this.modelFields);
      const commentHtml = trashCommentField
        ? deleteCommentFieldHtml(this.$t.bind(this), trashCommentField)
        : "";

      const result = await Swal.fire({
        title: this.$tn("Permanently delete?"),
        html: trashCommentField
          ? buildTrashDeleteConfirmHtml({
              messageHtml: `<p>${this.$tn("Are you sure you want to permanently delete this")} ${this.selectedModelData?.label || this.$tn("record")}?</p><p class="text-danger mb-0">${this.$tn("This action cannot be undone.")}</p>`,
              commentHtml,
            })
          : `${this.$tn("Are you sure you want to permanently delete this")} ${this.selectedModelData?.label || this.$tn("record")}?<br><br><span class="text-danger">${this.$tn("This action cannot be undone.")}</span>`,
        ...(trashCommentField ? getTrashDeleteCommentSwalOptions() : {}),
        ...(trashCommentField ? { preConfirm: trashDeleteCommentPreConfirm } : {}),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$tn("Yes, permanently delete"),
        cancelButtonText: this.$tn("Cancel"),
      });

      if (result.isConfirmed) {
        const trashDeleteComment = trashCommentField
          ? trashDeleteCommentFromSwalResult(result)
          : "";
        const deleteConfig = trashDeleteComment
          ? buildSoftDeleteRequestConfig(trashDeleteComment)
          : {};
        try {
          showTrashDeleteLoadingSwal();
          try {
            await wencesApi.delete(
              `/api/gotrading/dynamic/${this.selectedModel}/${this.item._id}/permanent`,
              deleteConfig
            );
          } finally {
            closeTrashDeleteLoadingSwal();
          }
          Swal.fire({
            title: this.$tn("Deleted"),
            text: this.$tn("The item has been permanently deleted."),
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
          // Redirect to the list view after a short delay
          setTimeout(() => {
            this.$router.push({
              name: "DynamicModel",
              params: { modelName: this.selectedModel },
            });
          }, 1500);
        } catch (error) {
          Swal.fire(this.$tn("Error"), this.$tn("An error occurred while deleting the item."), "error");
        }
      }
    },
    formatSubmissionDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const day = days[date.getDay()];
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();
      const hh = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      return `${day}, ${dd}/${mm}/${yyyy} - ${hh}:${min}`;
    },

    isGroupModel() {
      return this.selectedModelData && this.selectedModelData.isGroupModel;
    },

    // Método para ir al modelo padre
    goToParentModel() {
      if (this.isGroupModel() && this.selectedModelData && this.item) {
        const parentModel =
          this.selectedModelData.parentModel ||
          this.selectedModel.split("_")[0];
        const parentField =
          this.selectedModelData.parentField ||
          this.selectedModel.split("_")[1];

        // Verificar si el item tiene un parent
        if (this.item.parent) {
          console.log(
            "🔍 [GO_TO_PARENT] Navegando al item padre:",
            parentModel,
            this.item.parent
          );

          // Navegar a la vista del item padre
          this.$router.push({
            name: "DynamicModelDetail",
            params: {
              modelName: parentModel,
              itemId: this.item.parent,
            },
          });
        } else {
          console.log(
            "⚠️ [GO_TO_PARENT] El item no tiene parent, navegando al listado del modelo padre"
          );

          // Fallback: navegar al listado del modelo padre
          this.$router.push({
            name: "DynamicModel",
            params: {
              modelName: parentModel,
            },
          });
        }
      } else {
        console.log(
          "⚠️ [GO_TO_PARENT] No se puede navegar al padre: modelo de grupo no válido o item no cargado"
        );
      }
    },
    // True si el modelo tiene visual groups (mostrar contenido con ViewContent en tabs)
    isVisualGroup() {
      return this.sortedVisualGroups && this.sortedVisualGroups.length > 0;
    },
    fieldsForVisualGroup(groupName) {
      if (!this.modelFields?.length || !groupName) return [];
      return this.modelFields.filter((f) => f.visualGroup === groupName);
    },
    // Verificar si el usuario puede ver un grupo visual
    canViewVisualGroup(group) {
      // Si no hay restricciones, permitir por defecto
      if (!group.roleRestrictions || group.roleRestrictions.length === 0) {
        return true;
      }
      
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }
      
      // Si es admin, puede ver todo
      if (userRole === "admin") {
        return true;
      }
      
      // Buscar restricción para el rol del usuario
      const restriction = group.roleRestrictions.find(r => r.roleName === userRole);
      if (!restriction) {
        // Si no hay restricción específica, permitir por defecto
        return true;
      }
      
      return restriction.canView !== false; // Por defecto true si no está definido
    },
    // Método para cambiar el grupo visual activo
    setActiveVisualGroup(groupName) {
      // Si el grupo ya está activo, cerrarlo
      if (this.activeVisualGroup === groupName) {
        this.activeVisualGroup = null;
        return;
      }
      
      this.activeVisualGroup = groupName;
      
      // Cargar datos embebidos cuando se selecciona un tab _embed_
      if (groupName.startsWith('_embed_')) {
        const fieldKey = groupName.replace('_embed_', '');
        this.loadEmbedData(fieldKey);
      }
    },
    async loadEmbedData(fieldKey) {
      // Encontrar el campo
      const field = this.modelFields.find(f => f.key === fieldKey);
      if (!field || !this.item || !this.item[fieldKey]) {
        this.$set(this.embedData, fieldKey, []);
        return;
      }

      this.$set(this.isLoadingEmbed, fieldKey, true);

      try {
        const refModel = field.ref.toLowerCase();
        let itemIds = [];

        // Manejar tanto arrays como valores únicos
        if (field.isArray && Array.isArray(this.item[fieldKey])) {
          itemIds = this.item[fieldKey].map(item => {
            if (typeof item === 'string') return item;
            if (item && item._id) return item._id;
            return null;
          }).filter(Boolean);
        } else {
          // Valor único
          const singleValue = this.item[fieldKey];
          const singleId = typeof singleValue === 'string' ? singleValue : (singleValue && singleValue._id);
          if (singleId) {
            itemIds = [singleId];
          }
        }

        if (itemIds.length === 0) {
          this.$set(this.embedData, fieldKey, []);
          return;
        }

        // Cargar cada item usando el modelo de referencia del campo
        const itemPromises = itemIds.map(id => 
          wencesApi.get(`/api/gotrading/dynamic/${refModel}/${id}`)
            .then(res => res.data)
            .catch(err => {
              console.error(`Error loading ${refModel} ${id}:`, err);
              return null;
            })
        );

        const results = await Promise.all(itemPromises);
        this.$set(this.embedData, fieldKey, results.filter(Boolean));
      } catch (error) {
        console.error(`Error loading embed data for ${fieldKey}:`, error);
        this.$set(this.embedData, fieldKey, []);
      } finally {
        this.$set(this.isLoadingEmbed, fieldKey, false);
      }
    },

    // Método para inicializar el primer grupo visual (deshabilitado: no abrir tab automáticamente)
    initializeFirstVisualGroup() {
      // No abrir ningún tab por defecto; el usuario elige cuál ver
    },
    applyRouteVisualGroupHint() {
      if (!this.$route?.query || !this.isVisualGroup()) return;
      const requestedGroup = String(this.$route.query.openVisualGroup || "").trim().toLowerCase();
      if (requestedGroup) {
        const targetGroup = this.sortedVisualGroups.find(
          (group) =>
            group &&
            typeof group.name === "string" &&
            group.name.toLowerCase() === requestedGroup &&
            this.canViewVisualGroup(group)
        );
        if (targetGroup && this.activeVisualGroup !== targetGroup.name) {
          this.setActiveVisualGroup(targetGroup.name);
          return;
        }
      }
      const focusSelector = String(this.$route.query.focusON || "");
      if (focusSelector && !this.activeVisualGroup) {
        const firstAvailableGroup = this.sortedVisualGroups.find((group) =>
          this.canViewVisualGroup(group)
        );
        if (firstAvailableGroup) {
          this.setActiveVisualGroup(firstAvailableGroup.name);
        }
      }
    },
    async fetchSettings() {
      try {
        const { data } = await wencesApi.get("/api/settings");
        if (data && data.success && data.data) {
          this.settings = data.data;
          this.updateTitle();
        }
      } catch (error) {
        console.error("Error loading settings:", error);
      }
    },
    async tryAssignResponsableFromRoute(itemId, options = {}) {
      if (!itemId || this.embedded) return;
      if (!this.isPaymentCollectResponsablesModuleActive) return;
      if (!isPaymentCollectResponsableModel(this.selectedModel)) return;
      const shouldAssign =
        options.shouldAssign === true ||
        (options.shouldAssign !== false && shouldAssignResponsableFromRoute(this.$route));
      if (!shouldAssign) return;

      const cleanQuery = stripAssignResponsableQuery(this.$route);
      if (this.$route.query?.assign_responsable != null) {
        await this.$router.replace({
          path: this.$route.path,
          query: cleanQuery,
          hash: this.$route.hash,
        });
      }

      try {
        const result = await assignResponsableViaApi(this.selectedModel, itemId);
        const name = result?.responsable?.name || "";
        const nameSuffix = name ? ` (${name})` : "";
        const message = result?.alreadyAssigned
          ? this.$t("paymentCollectResponsable.alreadyAssigned")
          : this.$t("paymentCollectResponsable.assignedSuccess", { name: nameSuffix });
        await Swal.fire({
          icon: "success",
          title: this.$tn("success"),
          text: message,
          confirmButtonText: this.$tn("ok"),
        });
        await this.fetchItem(itemId);
      } catch (error) {
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text:
            error?.response?.data?.error ||
            error?.message ||
            this.$tn("paymentCollectResponsable.assignFailed"),
          confirmButtonText: this.$tn("ok"),
        });
      }
    },
    async fetchActiveModules() {
      try {
        const response = await wencesApi.get("/api/modules/active");

        // Intentar diferentes formatos de respuesta
        if (response.data && Array.isArray(response.data)) {
          this.activeModules = response.data;
        } else if (
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data)
        ) {
          this.activeModules = response.data.data;
        } else {
          this.activeModules = [];
        }
      } catch (error) {
        console.error("Error fetching active modules:", error);
        this.activeModules = [];
      }
    },
    async fetchUserRoles() {
      try {
        // Obtener todos los roles
        const response = await wencesApi.get("/api/roles");
        if (response.data && response.data.data) {
          this.userRoles = response.data.data;
        }
      } catch (error) {
        console.error("Error fetching user roles:", error);
        this.userRoles = [];
      }
    },
    updateTitle() {
      // Actualizar el título cuando hay un item con name y settings
      if (
        this.item &&
        this.item.name &&
        this.settings &&
        this.settings.siteName
      ) {
        document.title = `${this.item.name} | ${this.settings.siteName}`;
      }
    },
    async probeAutonomousCustomDetail(modelName, itemId) {
      if (!modelName || !itemId) return false;
      try {
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/${modelName}/form-schema`,
          { params: { mode: "edit", itemId } }
        );
        if (
          isAutonomousDynamicForm(data.modelName, "view", {
            customFormKey: data.meta?.customFormKey,
            trustCustomFormKey: true,
          })
        ) {
          this.useAutonomousCustomDetail = true;
          return true;
        }
      } catch (_e) {
        /* detalle estándar ViewContent */
      }
      this.useAutonomousCustomDetail = false;
      return false;
    },
  },
  beforeDestroy() {
    // El nombre publicado es de este item: al salir, que no lo herede otra pantalla.
    clearCurrentItemLabel();
  },
  async mounted() {
    await this.fetchModels();
    await this.fetchLists();
    await this.fetchSettings();
    await this.fetchActiveModules();
    await this.fetchUserRoles();

    // Si estamos en modo embebido, usar las props
    if (this.embedded && this.propModelName) {
      const found = this.models.find(
        (m) => m.modelName.toLowerCase() === this.propModelName.toLowerCase()
      );
      if (found) {
        this.selectedModel = found.modelName;
        this.selectedModelData = found;
        this.modelFields = found.fields;
        
        if (this.propItemData) {
          this.item = this.propItemData;
          this.loading = false;
        }
        
        await this.fetchModelViews();
      }
      
      this.$nextTick(() => {
        this.initializeFirstVisualGroup();
      });
      return;
    }

    const { modelName, itemId } = resolveDynamicModelRouteParams(
      this.$route,
      this.models.map((m) => m.modelName)
    );

    if (modelName && this.models.length) {
      const found = this.models.find(
        (m) => m.modelName.toLowerCase() === modelName.toLowerCase()
      );
      if (found) {
        this.selectedModel = found.modelName;
        this.selectedModelData = found;
        this.modelFields = found.fields;

        let autonomous = false;
        if (itemId) {
          autonomous = await this.probeAutonomousCustomDetail(modelName, itemId);
        }

        if (itemId) {
          const assignResponsableIntent = captureAssignResponsableIntent(this.$route);
          await Promise.all([this.fetchModelViews(), this.fetchItem(itemId)]);
          await this.tryAssignResponsableFromRoute(itemId, {
            shouldAssign: assignResponsableIntent,
          });
        } else {
          await this.fetchModelViews();
        }

        if (!autonomous) {
          this.$nextTick(() => {
            this.initializeFirstVisualGroup();
          });
        }
      }
    }

    this.loading = false;
  },
  async beforeRouteUpdate(to, from, next) {
    const assignIntent = captureAssignResponsableIntent(to);
    next();
    if (!assignIntent) return;
    const { modelName, itemId } = resolveDynamicModelRouteParams(
      to,
      (this.models || []).map((m) => m.modelName)
    );
    if (!modelName || !itemId) return;
    if (this.selectedModel && this.selectedModel.toLowerCase() !== modelName.toLowerCase()) {
      return;
    }
    if (!this.selectedModel) {
      this.selectedModel = modelName;
    }
    await this.tryAssignResponsableFromRoute(itemId, { shouldAssign: true });
  },
};
</script>
<style>
/* Estilo de tabs verticales similar a Form.vue */
.vertical-tabs-container {
  margin-bottom: 1rem;
}


.cursor-pointer {
  cursor: pointer;
}

/* Container del contenido de los tabs */
.view-content-container {
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  margin-top: 0;
  position: relative;
  z-index: 0;
}

/* Asegurar que los grupos se muestren correctamente */
.group-notarray {
  min-height: 200px;
  padding: 1.5rem;
}

/* Estilo para los contenedores de grupos visuales */
.group-notarray-group_ {
  position: relative;
  z-index: 0;
  margin: 0px 0px;
}

/* Responsive para pantallas pequeñas */
@media (max-width: 768px) {
  .group-notarray {
    padding: 1rem;
  }
}
</style>

<style scoped>
/* Escritorio: la página sin el recuadro azul; las secciones son tarjetas. */
.detail-page {
  padding-bottom: 1rem;
}

.detail-section-head {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
  padding: 10px 14px !important;
  border-radius: 10px;
  border-color: #e6e8ec !important;
  background-color: var(--bs-body-bg);
  font-weight: 600;
  font-size: 0.9rem;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.detail-section-head:not(.is-open):hover {
  background-color: rgba(var(--bs-primary-rgb), 0.03);
  border-color: rgba(var(--bs-primary-rgb), 0.3) !important;
  box-shadow: 0 2px 6px rgba(16, 24, 40, 0.08);
}

/* Ícono de la sección en una pastilla con tinte del primario. */
.detail-section-head > i:first-of-type {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-right: 8px !important;
  border-radius: 8px;
  font-size: 18px;
  color: var(--bs-primary);
  background-color: rgba(var(--bs-primary-rgb), 0.08);
}

/* La flecha va al final, como en un acordeón. */
.detail-section-head > i:nth-of-type(2) {
  order: 10;
  margin-left: auto;
  margin-right: 0 !important;
  color: #9aa1ad;
}

.detail-section-head.is-open {
  border-radius: 10px 10px 0 0;
  box-shadow: none;
}

.detail-section-head.is-open > i:first-of-type {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.2);
}

.detail-section-head.is-open > i:nth-of-type(2) {
  color: #fff;
}

.detail-section-head.is-open + .vertical-tab-content {
  margin-bottom: 8px;
  border: 1px solid #e6e8ec;
  border-top: 0;
  border-radius: 0 0 10px 10px;
  overflow: hidden;
  background-color: var(--bs-body-bg);
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
}

.detail-timeline-card {
  border-radius: 10px;
  border-color: #e6e8ec;
  font-weight: 600;
  font-size: 0.9rem;
}

.detail-timeline-card > div {
  padding: 12px 14px !important;
}

.detail-timeline-card:hover {
  border-color: rgba(var(--bs-primary-rgb), 0.3);
}

/* Grupos anidados (productos, contenedores): recuadro suave y redondeado. */
.detail-page ::v-deep .view-content-layout.border.border-primary {
  border-color: rgba(var(--bs-primary-rgb), 0.25) !important;
  border-radius: 8px;
}

/* Etiqueta del campo en gris chico, el valor se lleva la atención. */
.detail-page ::v-deep .view-content-layout small.form-label strong {
  font-weight: 500;
  font-size: 0.78rem;
  color: #6b7280;
}

/* Teléfono: alta y última edición, debajo de la barra (components/mobile). */
.m-detail-meta {
  margin: 0.4rem 0 0.6rem;
  font-size: 0.78rem;
  color: var(--m-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Teléfono: sin el recuadro de la página, todo el ancho para el contenido. */
.m-detail-page {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}
.card {
  margin-top: 1rem;
}

/* Botones de vistas públicas: son <a> para que el link sea real (pestaña nueva, copiar,
   recargar). El tema fuerza `a:hover{color: … !important}`, que pisaría el color de .btn. */
.gt-view-link.btn:hover,
.gt-view-link.btn:focus {
  color: var(--bs-btn-hover-color) !important;
}

.gt-view-link.btn.disabled {
  pointer-events: none;
}

/* Estilos para tabs verticales */
.vertical-tabs-container {
  width: 100%;
}

.shipping .form-label.bg-primary {
  background-color: #3bb748 !important;
  border-color: #3bb748 !important;
}

.proform .form-label.bg-primary {
  background-color: #ff7c7c !important;
  border-color: #ff7c7c !important;
}

.float-left {
  float: left;
  width: auto;
}
.btn-group {
  margin-left: 0.5rem;
}

.btn-group .btn {
  margin-right: 0.25rem;
}

/* Estilos para sección de Finance */
.finance-content-section {
  padding: 0;
}

.finance-content-section .card-title {
  color: #198754;
  font-weight: 600;
}
</style>
